import React from "react";
import type { GridColDef, GridColumnGroupingModel } from "@mui/x-data-grid-pro";
import { DataGrid } from "../../../shared/components/DataGrid";
import { usePopoutWindowContext } from "../../../shared/components/PopoutWindow";
import { useAppSelector } from "../../../store";
import { selectAggregate, type AggregateRow } from "../../../store/profiling/selectors";
import { formatMs } from "./viz";
import CodeBlock from "../CodeBlock";
import { CodeHoverLayer, type CodeHoverApi } from "../CodeHover";

// Right-aligned, tabular millisecond column; the raw numeric value drives sorting, `formatMs` only formats.
const msColumn = (field: keyof AggregateRow, headerName: string, description: string): GridColDef<AggregateRow> => ({
    field,
    headerName,
    description,
    type: "number",
    width: 96,
    valueFormatter: (value: number) => formatMs(value),
});

function AggregateView() {
    const rows = useAppSelector(selectAggregate);
    // Portal the hover tooltip into the pop-out window's body so it renders in the correct window.
    const container = usePopoutWindowContext()?.window?.document.body ?? undefined;
    const hoverRef = React.useRef<CodeHoverApi>(null);

    const columns = React.useMemo<GridColDef<AggregateRow>[]>(
        () => [
            { field: "label", headerName: "Node", flex: 1, minWidth: 140 },
            {
                field: "source",
                headerName: "Source",
                sortable: false,
                flex: 2,
                minWidth: 260,
                renderCell: params => (
                    <span
                        className="agg-source-cell"
                        onMouseMove={e => hoverRef.current?.move(params.row.source, e.clientX, e.clientY)}
                        onMouseLeave={() => hoverRef.current?.hide()}
                    >
                        <CodeBlock code={params.row.source} singleLine pinnable onPinToggle={() => hoverRef.current?.hide()} />
                    </span>
                ),
            },
            { field: "count", headerName: "Count", type: "number", width: 90, valueFormatter: (v: number) => v.toLocaleString(), description: "Total node hits across all render, callback and React batches." },
            msColumn("totalSelfTime", "Σ self", "Cumulative self (exclusive) time across all passes."),
            msColumn("avgSelfTime", "Avg self", "Mean self time per hit."),
            msColumn("totalTime", "Σ total", "Cumulative total (inclusive) time across all passes."),
            { field: "reactCount", headerName: "Commits", type: "number", width: 96, valueFormatter: (v: number) => (v ? v.toLocaleString() : "—"), description: "React commits that rendered this node (requires React render timing)." },
            msColumn("avgCommitTime", "Avg commit", "Mean React actualDuration per commit."),
            msColumn("totalCommitTime", "Σ commit", "Cumulative React actualDuration across commits."),
        ],
        []
    );

    const columnGroupingModel = React.useMemo<GridColumnGroupingModel>(
        () => [
            { groupId: "parser", headerName: "Parser", children: [{ field: "count" }, { field: "totalSelfTime" }, { field: "avgSelfTime" }, { field: "totalTime" }] },
            { groupId: "react", headerName: "React commit", children: [{ field: "reactCount" }, { field: "avgCommitTime" }, { field: "totalCommitTime" }] },
        ],
        []
    );

    if (rows.length === 0) {
        return <div className="aggregate-empty">No profiling data captured yet. Enable profiling and interact with the canvas.</div>;
    }

    return (
        <div className="aggregate-view">
            <div className="aggregate-caption">
                Hotspots across all retained render passes, grouped by source location. Parser columns cover every render/callback/React
                batch; React commit columns populate only while "React render timing" is enabled.
            </div>
            <DataGrid
                className="aggregate-grid"
                rows={rows}
                columns={columns}
                columnGroupingModel={columnGroupingModel}
                getRowId={r => r.key}
                disableRowSelectionOnClick
                disableColumnMenu
                density="compact"
                rowHeight={36}
                initialState={{ sorting: { sortModel: [{ field: "totalSelfTime", sort: "desc" }] } }}
                sx={{ flex: 1, minHeight: 0 }}
            />
            <CodeHoverLayer ref={hoverRef} container={container} />
        </div>
    );
}

export default React.memo(AggregateView);
